/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

// Adds `?` to every type PROPERTY whose type is nullable (a union containing
// `null`) or is `any`/`unknown`. This reproduces the v4/5 typescript-operations
// output shape under v6, where nullable result fields are emitted as required.
// Operates only on PropertySignature nodes (type members) — never on runtime
// code — and edits by byte offset so nothing else in the source is reformatted.
//
// `addOptionals` is a pure string -> string transform so it can run both:
//   - in-process from `codegen.ts` (via the `beforeOneFileWrite` hook), and
//   - as a standalone CLI (`node scripts/add-optionals.ts`) that rewrites the
//     generated files in `src/generated` in place.
import ts from "typescript";

// A property should become optional if its type is nullable (a union containing
// `null`) OR is `any`/`unknown`. The `any`/`unknown` case matters because a
// nullable custom-scalar field (e.g. `Date`) is emitted as `any | null`, which
// TypeScript collapses to `any` — losing the `| null` marker — so we can't rely
// on the union check alone. Treating `any`/`unknown` as optional reproduces the
// v4/v5 shape where those nullable scalar fields carried `?`.
const isNullLiteral = (t: ts.TypeNode): boolean =>
	ts.isLiteralTypeNode(t) && t.literal.kind === ts.SyntaxKind.NullKeyword;

const isAnyOrUnknown = (t: ts.TypeNode): boolean =>
	t.kind === ts.SyntaxKind.AnyKeyword || t.kind === ts.SyntaxKind.UnknownKeyword;

const typeIsNullable = (typeNode: ts.TypeNode | undefined): boolean => {
	if (!typeNode) return false;
	if (ts.isUnionTypeNode(typeNode)) {
		return typeNode.types.some((t) => isNullLiteral(t) || isAnyOrUnknown(t));
	}
	return isNullLiteral(typeNode) || isAnyOrUnknown(typeNode);
};

/**
 * Insert `?` after the name of every non-optional PropertySignature whose type
 * is nullable / `any` / `unknown`. Returns the transformed source; the input is
 * returned unchanged when there is nothing to do.
 */
export const addOptionals = (content: string): string => {
	const sf = ts.createSourceFile("gen.ts", content, ts.ScriptTarget.Latest, true);

	const insertions: number[] = []; // offsets where we insert "?"

	const visit = (node: ts.Node): void => {
		if (ts.isPropertySignature(node) && !node.questionToken && typeIsNullable(node.type)) {
			insertions.push(node.name.getEnd());
		}
		ts.forEachChild(node, visit);
	};
	visit(sf);

	if (insertions.length === 0) return content;

	insertions.sort((a, b) => b - a); // apply right-to-left so offsets stay valid
	let out = content;
	for (const pos of insertions) out = out.slice(0, pos) + "?" + out.slice(pos);
	return out;
};
